import {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react';
import type {Schema} from 'schemaDefinition/types';
import type {ColumnWithLabel} from 'services/advancedSearch/types';
import type {DataGridSettings} from 'services/settings/types';
import {useAllColumns} from 'services/advancedSearch';
import {arrayIsEqual} from 'services/utils/functions';
import {advancedSearchDefaultColumns} from 'schemas/configurations/table/columns';
import type {ColumnConfigVisibility, CurrentColumnsType} from '../types';
import {advancedSearchSchemaName} from '../types';
import {mapDataColumnsToSchema} from '../mappers/mapDataColumnsToSchema';
import {useDataTranslations} from './useDataTranslations';

const defaultColumnNames = advancedSearchDefaultColumns.map(c => c.name);

const getStoredColumns = (
  storedColumns: DataGridSettings['columns'] | undefined,
): string[] | undefined => {
  const columns = storedColumns?.orderedFields?.filter(
    field => storedColumns.columnVisibilityModel?.[field] !== false,
  );
  return columns && columns.length > 0 ? columns : undefined;
};

export const useCurrentColumns = (
  storedColumns?: DataGridSettings['columns'],
): CurrentColumnsType => {
  const [currentColumns, setCurrentColumnsState] = useState<string[]>(
    () => getStoredColumns(storedColumns) ?? defaultColumnNames,
  );

  const updateCurrentColumns = useCallback(
    (updates: ColumnConfigVisibility[]) => {
      setCurrentColumnsState(prev => {
        const removed = new Set(
          updates.filter(u => !u.visible).map(u => u.name),
        );
        const added = updates
          .filter(u => u.visible && !prev.includes(u.name))
          .map(u => u.name);
        const next = [...prev.filter(c => !removed.has(c)), ...added];

        return arrayIsEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const setCurrentColumns = useCallback((columns: string[]) => {
    setCurrentColumnsState(prev => {
      // Keep the order of already selected columns, append new ones
      const next = [
        ...prev.filter(c => columns.includes(c)),
        ...columns.filter(c => !prev.includes(c)),
      ];

      return arrayIsEqual(prev, next) ? prev : next;
    });
  }, []);

  const handleAddCurrentColumn = useCallback(
    (column: string) => updateCurrentColumns([{name: column, visible: true}]),
    [updateCurrentColumns],
  );

  const handleRemoveCurrentColumn = useCallback(
    (column: string) => updateCurrentColumns([{name: column, visible: false}]),
    [updateCurrentColumns],
  );

  const {getLabel} = useDataTranslations();
  const [schema, setSchema] = useState<Schema>(() => ({
    name: advancedSearchSchemaName,
    parts: currentColumns.map(name => ({name, type: 'text'})),
  }));
  const onSetSchema = useEffectEvent(setSchema);

  const {isLoading: isLoadingAllColumns, data: allColumns} = useAllColumns();

  // Update schema, when column selection changes.
  useEffect(() => {
    if (allColumns !== undefined) {
      onSetSchema(
        mapDataColumnsToSchema(
          advancedSearchSchemaName,
          allColumns,
          currentColumns,
        ),
      );
    }
  }, [allColumns, currentColumns]);

  const allColumnsWithLabel: ColumnWithLabel[] = useMemo(() => {
    return (
      allColumns
        ?.map(d => {
          return {
            ...d,
            label: getLabel(d.name),
          };
        })
        .sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0)) ??
      []
    );
  }, [allColumns, getLabel]);

  return useMemo(() => {
    return {
      schema,
      allColumns: allColumnsWithLabel,
      loadingColumns: isLoadingAllColumns,
      currentColumns,
      addCurrentColumn: handleAddCurrentColumn,
      removeCurrentColumn: handleRemoveCurrentColumn,
      updateCurrentColumns,
      setCurrentColumns,
    };
  }, [
    allColumnsWithLabel,
    currentColumns,
    handleAddCurrentColumn,
    handleRemoveCurrentColumn,
    updateCurrentColumns,
    schema,
    setCurrentColumns,
    isLoadingAllColumns,
  ]);
};
